import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { MoneyField } from '@/components/common';
import type { LoanScheduleResponse, LoanScheduleRequest, LoanScheduleItemVO } from '@/services/auditService/auditLoanCalculatorService';
import { calcBankLoanSchedule, calcVehicleLoanSchedule, recalculateLoanSchedule } from '@/services/auditService/auditLoanCalculatorService';
import { useCompanyAnnualStore } from '@/store/audit/companyAnnualStore';
import { inRange, parseAnyDate, sumByRange, addDays, addYears, formatDDMMYYYY } from '@/utils/dataUtils';
import {
  clipBankLoanScheduleAtEarlyRepay,
  sumsFromScheduleHighlightWindows,
  type BankLoanSumPeriodWindow,
} from '@/pages/components/audit/adjustment/adjust/dialogs/loan/bankLoanSumCalc';

export interface BankLoanSums {
  interestYoa: number;
  currentLastYoa: number;
  interestLastYoa: number;
  currentYoa: number;
  nonCurrentYoa: number;
  interestNext: number;
  /** Client-only: last Balance in blue (last-year) window → Summary Total lastYoa (split left). */
  balanceLastYoa?: number;
  /** Summary Total lastYoa manual override (split right) — same persist pattern as other *Change. */
  balanceLastYoaChange?: number | null;
  interestYoaChange?: number | null;
  interestLastYoaChange?: number | null;
  currentYoaChange?: number | null;
  currentLastYoaChange?: number | null;
  nonCurrentYoaChange?: number | null;
  prevFinalBalance?: number | null;
  currFinalBalance?: number | null;
  editedInstalments?: string | null;
}

export interface VehicleLoanSums {
  interestYoa: number;
  interestLastYoa: number;
  currentYoa: number;
  currentLastYoa: number;
  nonCurrentYoa: number;
  nonCurrentLastYoa?: number;
  balanceYoa?: number;
  balanceLastYoa?: number;
  interestYoaChange?: number | null;
  interestLastYoaChange?: number | null;
  currentYoaChange?: number | null;
  currentLastYoaChange?: number | null;
  nonCurrentYoaChange?: number | null;
  balanceLastYoaChange?: number | null;
  prevFinalBalance?: number | null;
  currFinalBalance?: number | null;
  editedInstalments?: string | null;
}

export interface RepaymentScheduleDrawerDataLoaded {
  scheduleData: LoanScheduleResponse;
  sums: BankLoanSums | VehicleLoanSums;
  periods: {
    periodFrom: string | null;
    periodTo: string | null;
    lastYearPeriodFrom: string | null;
    lastYearPeriodTo: string | null;
    nextYearPeriodFrom: string | null;
    nextYearPeriodTo: string | null;
  };
  /**
   * True when sums were built from recalculatedSchedule / user recalc rows
   * (not the raw pre-override schedule). BankLoanDetail uses this to refresh
   * Summary split-left bases while saved schedule overrides exist.
   */
  appliedRecalculatedSchedule?: boolean;
}

export interface RepaymentScheduleDrawerProps {
  open: boolean;
  onClose: () => void;
  // Loan type: bank or vehicle
  loanType: 'bank' | 'vehicle';
  // Annual ID to fetch period ranges
  annualId?: number;
  // Loan schedule request parameters
  scheduleRequest?: LoanScheduleRequest | null;
  // Callback to notify parent when data is loaded
  onDataLoaded?: (data: RepaymentScheduleDrawerDataLoaded) => void;
  // Initial overrides from previously saved recalculation edits
  initialOverrides?: {
    prevFinalBalance?: number | null;
    currFinalBalance?: number | null;
    editedInstalments?: string | null;
  } | null;
  // Notify parent of drawer Paper rendered width (0 when closed), used to position sibling Dialogs to avoid overlap
  onWidthChange?: (width: number) => void;
  // When true, display schedule in read-only mode (no editing of instalments or balances)
  readOnly?: boolean;
  // Pre-loaded schedule data (bypasses API call when provided)
  directScheduleData?: LoanScheduleResponse | null;
  /** DD/MM/YYYY. Instalments after this date are excluded from schedule sums. */
  earlyRepay?: string | null;
  /**
   * MULTI YOA Tab only. When set, overrides currentYear / lastYear windows.
   * SINGLE must leave this null/undefined so getYearOfAssessment(annualId) path stays unchanged.
   */
  periodWindow?: BankLoanSumPeriodWindow | null;
  /**
   * BankLoan Summary calc-side hover: scroll + red-outline the matching period column cells
   * and show their sum beside Interest rate p.a. (%).
   */
  summaryTrace?: BankLoanScheduleSummaryTrace | null;
}

/** SummaryCard calc value → RepaymentScheduleDrawer period/column (green/blue/yellow). */
export type BankLoanScheduleSummaryTrace = {
  /** curr=green, last=blue, next=yellow */
  period: 'curr' | 'last' | 'next';
  column: 'instalment' | 'interest' | 'principal' | 'balance';
  /** sum all rows in period; last = only the last row in that period */
  mode: 'sum' | 'last';
};

const number = (v: number) =>
  (Number.isFinite(v) ? v : 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Default prevFinalBalance when user has not overridden it.
 * Prefer last inPrev Balance; if no last-year rows, use prior instalment closing
 * (startNo-1) else Loan Amount — avoid 0 so bank interest is not wiped.
 */
function resolvePrevFinalBalanceFallback(
  schedule: LoanScheduleItemVO[],
  lastPrevNo: number,
  startCurrNo: number | undefined,
  loanAmount: number | undefined,
): number {
  if (lastPrevNo > 0) {
    const prevRow = schedule.find((r) => r.instalmentNo === lastPrevNo);
    if (prevRow?.outstandingBalance != null) return prevRow.outstandingBalance;
  }
  if (startCurrNo != null && startCurrNo > 1) {
    const prior = schedule.find((r) => r.instalmentNo === startCurrNo - 1);
    if (prior?.outstandingBalance != null) return prior.outstandingBalance;
  }
  return loanAmount ?? 0;
}

export default function RepaymentScheduleDrawer({ 
  open, 
  onClose, 
  loanType, 
  annualId, 
  scheduleRequest,
  onDataLoaded,
  initialOverrides,
  onWidthChange,
  readOnly = false,
  directScheduleData,
  earlyRepay,
  periodWindow = null,
  summaryTrace = null,
}: RepaymentScheduleDrawerProps) {
  // Ref to drawer Paper element so we can measure its rendered width
  const paperRef = useRef<HTMLDivElement | null>(null);

  // Report drawer Paper width to parent when open/close or table content resizes
  useEffect(() => {
    if (!onWidthChange) return;
    if (!open) {
      onWidthChange(0);
      return;
    }
    const measure = () => {
      const el = paperRef.current;
      if (!el) return;
      onWidthChange(el.getBoundingClientRect().width);
    };
    // Measure immediately and after a short delay to catch transition end
    measure();
    const t = setTimeout(measure, 250);
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined' && paperRef.current) {
      ro = new ResizeObserver(measure);
      ro.observe(paperRef.current);
    }
    window.addEventListener('resize', measure);
    return () => {
      clearTimeout(t);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [open, onWidthChange]);
  // Parse initial overrides
  const parsedInitialInstalments = useMemo(() => {
    if (!initialOverrides?.editedInstalments) return null;
    try {
      const parsed = JSON.parse(initialOverrides.editedInstalments);
      if (typeof parsed === 'object' && parsed !== null) return parsed as Record<number, number>;
    } catch { /* ignore */ }
    return null;
  }, [initialOverrides?.editedInstalments]);

  // Schedule data state
  const [data, setData] = useState<LoanScheduleResponse | null>(null);
  
  // Highlight state
  const [highlightInstalments, setHighlightInstalments] = useState<number[]>([]);
  const [highlightInstalmentsLastYear, setHighlightInstalmentsLastYear] = useState<number[]>([]);
  const [highlightInstalmentsNextYear, setHighlightInstalmentsNextYear] = useState<number[]>([]);
  
  // Period ranges state
  const [periodFrom, setPeriodFrom] = useState<string | null>(null);
  const [periodTo, setPeriodTo] = useState<string | null>(null);
  const [lastYearPeriodFrom, setLastYearPeriodFrom] = useState<string | null>(null);
  const [lastYearPeriodTo, setLastYearPeriodTo] = useState<string | null>(null);
  const [nextYearPeriodFrom, setNextYearPeriodFrom] = useState<string | null>(null);
  const [nextYearPeriodTo, setNextYearPeriodTo] = useState<string | null>(null);

  // Editable overrides: null = unset (not 0 — 0 is a valid override)
  const [editedPrevFinalBalance, setEditedPrevFinalBalance] = useState<number | null>(null);
  const [editedCurrInstalments, setEditedCurrInstalments] = useState<Record<number, number>>({});
  const [editedCurrFinalBalance, setEditedCurrFinalBalance] = useState<number | null>(null);
  const editedPrevFinalBalanceRef = useRef(editedPrevFinalBalance);
  const editedCurrInstalmentsRef = useRef(editedCurrInstalments);
  const editedCurrFinalBalanceRef = useRef(editedCurrFinalBalance);
  editedPrevFinalBalanceRef.current = editedPrevFinalBalance;
  editedCurrInstalmentsRef.current = editedCurrInstalments;
  editedCurrFinalBalanceRef.current = editedCurrFinalBalance;

  // Recalculated data state
  const [recalculatedData, setRecalculatedData] = useState<LoanScheduleItemVO[] | null>(null);
  const [isRecalculating, setIsRecalculating] = useState(false);

  // Debounce timer ref for auto-recalculate
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track whether user has made any edits (to avoid auto-recalc on initial load)
  const hasUserEditedRef = useRef(false);
  /** Restore saved overrides into edit UI only — do NOT treat as user edit (no standalone recalculate on init). */
  const initialOverridesAppliedRef = useRef(false);
  /**
   * Init calcBankLoanSchedule / calcVehicleLoanSchedule: once per content key.
   * Parent re-renders (onWidthChange, onDataLoaded → setSums, new initialOverrides object)
   * must not re-hit the API.
   */
  const scheduleInitFetchKeyRef = useRef<string | null>(null);
  const scheduleInitFetchGenRef = useRef(0);
  /** Dedup local highlight/onDataLoaded when effect re-enters with same period snapshot. */
  const lastHighlightNotifyKeyRef = useRef<string | null>(null);
  const onDataLoadedRef = useRef(onDataLoaded);
  onDataLoadedRef.current = onDataLoaded;
  const periodFromRef = useRef(periodFrom);
  const periodToRef = useRef(periodTo);
  const lastYearPeriodFromRef = useRef(lastYearPeriodFrom);
  const lastYearPeriodToRef = useRef(lastYearPeriodTo);
  const nextYearPeriodFromRef = useRef(nextYearPeriodFrom);
  const nextYearPeriodToRef = useRef(nextYearPeriodTo);
  const earlyRepayRef = useRef(earlyRepay);
  const dataRef = useRef(data);
  const recalculatedDataRef = useRef(recalculatedData);
  periodFromRef.current = periodFrom;
  periodToRef.current = periodTo;
  lastYearPeriodFromRef.current = lastYearPeriodFrom;
  lastYearPeriodToRef.current = lastYearPeriodTo;
  nextYearPeriodFromRef.current = nextYearPeriodFrom;
  nextYearPeriodToRef.current = nextYearPeriodTo;
  earlyRepayRef.current = earlyRepay;
  dataRef.current = data;
  recalculatedDataRef.current = recalculatedData;

  // Derived: last instalment numbers in each period
  const lastPrevNo = useMemo(() => highlightInstalmentsLastYear.length > 0
    ? highlightInstalmentsLastYear[highlightInstalmentsLastYear.length - 1] : -1, [highlightInstalmentsLastYear]);
  const lastCurrNo = useMemo(() => highlightInstalments.length > 0
    ? highlightInstalments[highlightInstalments.length - 1] : -1, [highlightInstalments]);
  const lastNextNo = useMemo(() => highlightInstalmentsNextYear.length > 0
    ? highlightInstalmentsNextYear[highlightInstalmentsNextYear.length - 1] : -1, [highlightInstalmentsNextYear]);

  const tableContainerRef = useRef<HTMLDivElement | null>(null);

  // Recalculated data lookup map by instalmentNo
  const recalcMap = useMemo(() => {
    if (!recalculatedData) return {} as Record<number, LoanScheduleItemVO>;
    const map: Record<number, LoanScheduleItemVO> = {};
    recalculatedData.forEach(item => { map[item.instalmentNo] = item; });
    return map;
  }, [recalculatedData]);

  const periodInstalmentNos = useCallback((period: BankLoanScheduleSummaryTrace['period']): number[] => {
    if (period === 'curr') return highlightInstalments;
    if (period === 'last') return highlightInstalmentsLastYear;
    return highlightInstalmentsNextYear;
  }, [highlightInstalments, highlightInstalmentsLastYear, highlightInstalmentsNextYear]);

  const isTraceRow = useCallback((instalmentNo: number): boolean => {
    if (!summaryTrace) return false;
    const nos = periodInstalmentNos(summaryTrace.period);
    if (!nos.includes(instalmentNo)) return false;
    if (summaryTrace.mode === 'last') {
      return instalmentNo === nos[nos.length - 1];
    }
    return true;
  }, [summaryTrace, periodInstalmentNos]);

  const resolveTraceCellValue = useCallback((row: LoanScheduleItemVO): number => {
    if (!summaryTrace) return 0;
    const recalcRow = recalcMap[row.instalmentNo];
    if (summaryTrace.column === 'instalment') {
      if (editedCurrInstalments[row.instalmentNo] != null) return editedCurrInstalments[row.instalmentNo];
      return recalcRow?.instalmentAmount ?? row.instalmentAmount ?? 0;
    }
    if (summaryTrace.column === 'interest') {
      return recalcRow?.interestPayment ?? row.interestPayment ?? 0;
    }
    if (summaryTrace.column === 'principal') {
      return recalcRow?.principalPayment ?? row.principalPayment ?? 0;
    }
    if (summaryTrace.column === 'balance') {
      if (row.instalmentNo === lastPrevNo && editedPrevFinalBalance != null) return editedPrevFinalBalance;
      if (row.instalmentNo === lastCurrNo && editedCurrFinalBalance != null) return editedCurrFinalBalance;
      return recalcRow?.outstandingBalance ?? row.outstandingBalance ?? 0;
    }
    return 0;
  }, [summaryTrace, recalcMap, editedCurrInstalments, editedPrevFinalBalance, editedCurrFinalBalance, lastPrevNo, lastCurrNo]);

  const summaryTraceSum = useMemo(() => {
    if (!summaryTrace || !data?.schedule?.length) return null;
    const rows = data.schedule.filter((r) => isTraceRow(r.instalmentNo));
    if (rows.length === 0) return null;
    return rows.reduce((acc, r) => acc + resolveTraceCellValue(r), 0);
  }, [summaryTrace, data?.schedule, isTraceRow, resolveTraceCellValue]);

  // Scroll first traced row into view when Summary hover starts
  useEffect(() => {
    if (!summaryTrace || !open) return;
    const nos = periodInstalmentNos(summaryTrace.period);
    const targetNo = summaryTrace.mode === 'last'
      ? (nos.length > 0 ? nos[nos.length - 1] : null)
      : (nos.length > 0 ? nos[0] : null);
    if (targetNo == null) return;
    const t = window.setTimeout(() => {
      const el = tableContainerRef.current?.querySelector(`[data-instalment-no="${targetNo}"]`);
      el?.scrollIntoView({behavior: 'smooth', block: 'center'});
    }, 50);
    return () => window.clearTimeout(t);
  }, [summaryTrace, open, periodInstalmentNos]);

  const TRACE_CELL_SX = {
    outline: '2px solid #f44336',
    outlineOffset: '-2px',
    animation: 'bankLoanSummaryTracePulse 1.1s ease-in-out infinite',
    '@keyframes bankLoanSummaryTracePulse': {
      '0%, 100%': {outlineColor: 'rgba(244, 67, 54, 0.35)'},
      '50%': {outlineColor: 'rgba(244, 67, 54, 1)'},
    },
  } as const;

  // Handle instalment change with cascading to subsequent inCurr rows (0 is a valid override)
  const handleInstalmentChange = useCallback((instalmentNo: number, value: number | null) => {
    const safeValue = Number(value);
    if (!Number.isFinite(safeValue)) return;
    setEditedCurrInstalments(prev => {
      const next = { ...prev };
      const idx = highlightInstalments.indexOf(instalmentNo);
      if (idx < 0) return prev;
      // Set this row and all subsequent inCurr rows to the same value
      for (let i = idx; i < highlightInstalments.length; i++) {
        next[highlightInstalments[i]] = safeValue;
      }
      return next;
    });
    // Clear recalculated data when user edits
    setRecalculatedData(null);
    hasUserEditedRef.current = true;
  }, [highlightInstalments]);

  // Handle prev final balance change (null clears; 0 is a valid override)
  const handlePrevFinalBalanceChange = useCallback((value: number | null) => {
    const n = value == null ? null : Number(value);
    setEditedPrevFinalBalance(n != null && Number.isFinite(n) ? n : null);
    setRecalculatedData(null);
    hasUserEditedRef.current = true;
  }, []);

  // Handle curr final balance change (null clears; 0 is a valid override)
  const handleCurrFinalBalanceChange = useCallback((value: number | null) => {
    const n = value == null ? null : Number(value);
    setEditedCurrFinalBalance(n != null && Number.isFinite(n) ? n : null);
    setRecalculatedData(null);
    hasUserEditedRef.current = true;
  }, []);

  // Handle cancel edit for instalment
  const handleInstalmentCancel = useCallback((instalmentNo: number) => {
    setEditedCurrInstalments(prev => {
      const next = { ...prev };
      delete next[instalmentNo];
      return next;
    });
    setRecalculatedData(null);
    hasUserEditedRef.current = true;
  }, []);

  // Handle cancel edit for prev final balance
  const handlePrevFinalBalanceCancel = useCallback(() => {
    setEditedPrevFinalBalance(null);
    setRecalculatedData(null);
    hasUserEditedRef.current = true;
  }, []);

  // Handle cancel edit for curr final balance
  const handleCurrFinalBalanceCancel = useCallback(() => {
    setEditedCurrFinalBalance(null);
    setRecalculatedData(null);
    hasUserEditedRef.current = true;
  }, []);

  // Handle recalculate
  const handleRecalculate = useCallback(async () => {
    if (!data || highlightInstalments.length === 0) return;

    const hasOverride =
      editedPrevFinalBalance != null
      || editedCurrFinalBalance != null
      || Object.keys(editedCurrInstalments).length > 0;

    const currFromDate = parseAnyDate(periodFrom ?? undefined);
    const currToDate = parseAnyDate(periodTo ?? undefined);
    const pt = parseAnyDate(periodTo || undefined);
    const nextPeriodFrom = pt ? addDays(pt, 1) : null;
    const nextPeriodTo = pt ? addYears(pt, 1) : null;

    /** Explicit null clears persisted schedule overrides; omit keys when user has not edited. 0 is a valid override. */
    const scheduleOverrideFields = (): Pick<BankLoanSums, 'prevFinalBalance' | 'currFinalBalance' | 'editedInstalments'> => {
      if (!hasUserEditedRef.current) return {};
      return {
        prevFinalBalance: editedPrevFinalBalance,
        currFinalBalance: editedCurrFinalBalance,
        editedInstalments: Object.keys(editedCurrInstalments).length > 0
          ? JSON.stringify(editedCurrInstalments)
          : null,
      };
    };

    const buildSumsFromSchedule = (schedule: LoanScheduleItemVO[]): BankLoanSums | VehicleLoanSums => {
      const overrides = scheduleOverrideFields();
      const highlighted = sumsFromScheduleHighlightWindows(
        schedule,
        highlightInstalmentsLastYear,
        highlightInstalmentsNextYear,
        editedPrevFinalBalance,
      );
      if (loanType === 'bank') {
        return {
          interestYoa: sumByRange(schedule, 'interestPayment', currFromDate, currToDate),
          currentLastYoa: sumByRange(schedule, 'principalPayment', currFromDate, currToDate),
          interestLastYoa: highlighted.interestLastYoa,
          currentYoa: highlighted.currentYoa,
          nonCurrentYoa: highlighted.nonCurrentYoa,
          interestNext: sumByRange(schedule, 'interestPayment', nextPeriodFrom, nextPeriodTo),
          balanceLastYoa: highlighted.balanceLastYoa,
          ...overrides,
        };
      }
      // Current(yoa)=next Principal sum; Current(lastYoa)=yoa Principal sum (align BankLoan <1yr)
      const currentLastYoa = sumByRange(schedule, 'principalPayment', currFromDate, currToDate);
      const interestYoa = sumByRange(schedule, 'interestPayment', currFromDate, currToDate);
      return {
        interestYoa,
        interestLastYoa: highlighted.interestLastYoa,
        currentYoa: highlighted.currentYoa,
        currentLastYoa,
        nonCurrentYoa: highlighted.nonCurrentYoa,
        balanceYoa: highlighted.currentYoa + highlighted.nonCurrentYoa,
        balanceLastYoa: highlighted.balanceLastYoa,
        nonCurrentLastYoa: (highlighted.balanceLastYoa ?? 0) - currentLastYoa,
        ...overrides,
      };
    };

    // Cancel cleared every override → restore original schedule calc (no recalc API).
    if (!hasOverride) {
      setRecalculatedData(null);
      const clipped = clipBankLoanScheduleAtEarlyRepay(data.schedule, earlyRepay);
      onDataLoaded?.({
        scheduleData: { ...data, schedule: clipped },
        sums: buildSumsFromSchedule(clipped),
        periods: { periodFrom, periodTo, lastYearPeriodFrom, lastYearPeriodTo, nextYearPeriodFrom, nextYearPeriodTo },
        appliedRecalculatedSchedule: false,
      });
      return;
    }

    setIsRecalculating(true);
    try {
      const totalRows = data.schedule.length;
      const remainingMonths = totalRows - lastCurrNo;
      const currInstalments = highlightInstalments.map(no => {
        if (editedCurrInstalments[no] != null) return editedCurrInstalments[no];
        const orig = data.schedule.find(r => r.instalmentNo === no);
        return orig?.instalmentAmount ?? 0;
      });

      // Opening for inCurr: last inPrev Balance; if no last-year rows, prior instalment
      // closing (startNo-1) else Loan Amount — never default to 0 (interest would be 0).
      const prevBal = editedPrevFinalBalance != null
        ? editedPrevFinalBalance
        : resolvePrevFinalBalanceFallback(
            data.schedule,
            lastPrevNo,
            highlightInstalments[0],
            data.loanAmount,
          );
      const currBal = editedCurrFinalBalance != null ? editedCurrFinalBalance
        : data.schedule.find(r => r.instalmentNo === lastCurrNo)?.outstandingBalance ?? 0;

      const startNo = highlightInstalments[0];
      const rowCount = currInstalments.length + Math.max(remainingMonths, 0);
      const repaymentDates = Array.from({ length: rowCount }, (_, i) => {
        const row = data.schedule.find((r) => r.instalmentNo === startNo + i);
        return row?.repaymentDate ?? '';
      });

      const result = await recalculateLoanSchedule({
        loanType,
        prevFinalBalance: prevBal,
        currFinalBalance: currBal,
        currInstalments,
        remainingMonths,
        annualInterestRatePercent: data.interestRatePercent ?? 0,
        startInstalmentNo: startNo,
        interestFreeFrom: scheduleRequest?.interestFreeFrom ?? null,
        interestFreeTo: scheduleRequest?.interestFreeTo ?? null,
        principalFreeFrom: scheduleRequest?.principalFreeFrom ?? null,
        principalFreeTo: scheduleRequest?.principalFreeTo ?? null,
        repaymentDates,
      });
      const recalcSchedule = result.schedule || [];
      setRecalculatedData(recalcSchedule);

      // Recalculate sums using recalculated data and notify parent
      if (recalcSchedule.length > 0 && data) {
        const recalcMapLocal: Record<number, LoanScheduleItemVO> = {};
        recalcSchedule.forEach(item => { recalcMapLocal[item.instalmentNo] = item; });
        const mergedSchedule = clipBankLoanScheduleAtEarlyRepay(
          data.schedule.map(row => {
            const recalcRow = recalcMapLocal[row.instalmentNo];
            return recalcRow ? { ...recalcRow, repaymentDate: row.repaymentDate } : row;
          }),
          earlyRepay,
        );

        onDataLoaded?.({
          scheduleData: { ...data, schedule: mergedSchedule },
          sums: buildSumsFromSchedule(mergedSchedule),
          periods: { periodFrom, periodTo, lastYearPeriodFrom, lastYearPeriodTo, nextYearPeriodFrom, nextYearPeriodTo },
          appliedRecalculatedSchedule: true,
        });
      }
    } catch (e) {
      console.error('Recalculate failed:', e);
    } finally {
      setIsRecalculating(false);
    }
  }, [
    data, highlightInstalments, lastCurrNo, lastPrevNo, editedCurrInstalments,
    editedPrevFinalBalance, editedCurrFinalBalance, loanType,
    highlightInstalmentsLastYear, highlightInstalmentsNextYear,
    periodFrom, periodTo, lastYearPeriodFrom, lastYearPeriodTo, nextYearPeriodFrom, nextYearPeriodTo,
    onDataLoaded, earlyRepay,
    scheduleRequest?.interestFreeFrom, scheduleRequest?.interestFreeTo,
    scheduleRequest?.principalFreeFrom, scheduleRequest?.principalFreeTo,
  ]);

  // Auto-recalculate with 1s debounce after user edits (or free-period change while overrides active)
  useEffect(() => {
    if (!hasUserEditedRef.current || !data || highlightInstalments.length === 0) return;

    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      handleRecalculate();
    }, 1000);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editedPrevFinalBalance, editedCurrInstalments, editedCurrFinalBalance,
    scheduleRequest?.interestFreeFrom, scheduleRequest?.interestFreeTo,
    scheduleRequest?.principalFreeFrom, scheduleRequest?.principalFreeTo,
  ]);

  // Restore saved overrides into edit UI only — do NOT treat as user edit (no recalculateLoanSchedule on init).
  useEffect(() => {
    if (!open) {
      hasUserEditedRef.current = false;
      initialOverridesAppliedRef.current = false;
      // Reset edited states when drawer closes (null = unset)
      setEditedPrevFinalBalance(null);
      setEditedCurrFinalBalance(null);
      setEditedCurrInstalments({});
      setRecalculatedData(null);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    }
  }, [open]);

  // Parent monthlyInstalment / base calc inputs changed → drop row overrides; fetchKey will re-hit calc*.
  const prevScheduleBaseKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open || !scheduleRequest) return;
    const baseKey = [
      scheduleRequest.loanAmount,
      scheduleRequest.tenorYears,
      scheduleRequest.firstRepaymentDate,
      scheduleRequest.totalInstalment ?? '',
      scheduleRequest.monthlyInstalment ?? '',
      scheduleRequest.annualInterestRatePercent ?? '',
    ].join('|');
    if (prevScheduleBaseKeyRef.current != null && prevScheduleBaseKeyRef.current !== baseKey) {
      setEditedCurrInstalments({});
      setEditedPrevFinalBalance(null);
      setEditedCurrFinalBalance(null);
      setRecalculatedData(null);
      hasUserEditedRef.current = false;
      initialOverridesAppliedRef.current = false;
      scheduleInitFetchKeyRef.current = null;
    }
    prevScheduleBaseKeyRef.current = baseKey;
  }, [
    open,
    scheduleRequest?.loanAmount,
    scheduleRequest?.tenorYears,
    scheduleRequest?.firstRepaymentDate,
    scheduleRequest?.totalInstalment,
    scheduleRequest?.monthlyInstalment,
    scheduleRequest?.annualInterestRatePercent,
  ]);

  useEffect(() => {
    if (!open || !data || highlightInstalments.length === 0) return;
    if (initialOverridesAppliedRef.current) return;

    const hasSavedOverrides = initialOverrides &&
      (initialOverrides.prevFinalBalance != null || initialOverrides.currFinalBalance != null || parsedInitialInstalments);

    if (hasSavedOverrides) {
      initialOverridesAppliedRef.current = true;
      if (initialOverrides.prevFinalBalance != null) {
        setEditedPrevFinalBalance(initialOverrides.prevFinalBalance);
      }
      if (initialOverrides.currFinalBalance != null) {
        setEditedCurrFinalBalance(initialOverrides.currFinalBalance);
      }
      if (parsedInitialInstalments) {
        setEditedCurrInstalments(parsedInitialInstalments);
      }
    }
  }, [open, data, highlightInstalments, initialOverrides, parsedInitialInstalments]);
  
  // MULTI YOA Tab only: caller-supplied windows for currentYear / lastYear (do not touch SINGLE).
  useEffect(() => {
    if (!open) return;
    if (!periodWindow || (periodWindow.periodFrom == null && periodWindow.periodTo == null)) return;

    setPeriodFrom(periodWindow.periodFrom ?? null);
    setPeriodTo(periodWindow.periodTo ?? null);
    setLastYearPeriodFrom(periodWindow.lastYearPeriodFrom ?? null);
    setLastYearPeriodTo(periodWindow.lastYearPeriodTo ?? null);
    const pt = parseAnyDate(periodWindow.periodTo || undefined);
    if (!pt) {
      setNextYearPeriodFrom(null);
      setNextYearPeriodTo(null);
      return;
    }
    setNextYearPeriodFrom(formatDDMMYYYY(addDays(pt, 1)));
    setNextYearPeriodTo(formatDDMMYYYY(addYears(pt, 1)));
  }, [
    open,
    periodWindow?.periodFrom,
    periodWindow?.periodTo,
    periodWindow?.lastYearPeriodFrom,
    periodWindow?.lastYearPeriodTo,
  ]);

  // SINGLE / default: Fetch period ranges from annual store (unchanged path).
  useEffect(() => {
    // Skip when MULTI Tab override is active — leave annual logic untouched for SINGLE.
    if (periodWindow && (periodWindow.periodFrom != null || periodWindow.periodTo != null)) return;
    if (!annualId || !open) return;
    let cancelled = false;
    const store = useCompanyAnnualStore.getState();
    (async () => {
      try {
        const yoa = await store.getYearOfAssessment(annualId);
        if (cancelled || !yoa) return;
        setPeriodFrom(yoa.periodFrom ?? null);
        setPeriodTo(yoa.periodTo ?? null);
        setLastYearPeriodFrom(yoa.lastYearPeriodFrom ?? null);
        setLastYearPeriodTo(yoa.lastYearPeriodTo ?? null);
        setNextYearPeriodFrom(yoa.nextYearPeriodFrom ?? null);
        setNextYearPeriodTo(yoa.nextYearPeriodTo ?? null);
      } catch (e) {
        // ignore errors
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    annualId,
    open,
    periodWindow?.periodFrom,
    periodWindow?.periodTo,
  ]);
  
  // Fetch schedule once per content key; YOA period arrival / parent re-render only refresh highlights locally.
  useEffect(() => {
    if (!open) {
      scheduleInitFetchKeyRef.current = null;
      scheduleInitFetchGenRef.current += 1;
      lastHighlightNotifyKeyRef.current = null;
      return;
    }

    const notifyHighlightsAndSums = (
      base: LoanScheduleResponse,
      recalcSchedule: LoanScheduleItemVO[],
    ) => {
      const pf = periodFromRef.current;
      const pt = periodToRef.current;
      const lyFrom = lastYearPeriodFromRef.current;
      const lyTo = lastYearPeriodToRef.current;
      const nyFrom = nextYearPeriodFromRef.current;
      const nyTo = nextYearPeriodToRef.current;
      const early = earlyRepayRef.current;
      const highlightKey = [
        pf ?? '', pt ?? '', lyFrom ?? '', lyTo ?? '', nyFrom ?? '', nyTo ?? '', early ?? '',
        base.loanAmount ?? '', base.repaymentPeriodYears ?? '', recalcSchedule.length,
      ].join('|');
      if (lastHighlightNotifyKeyRef.current === highlightKey) return;
      lastHighlightNotifyKeyRef.current = highlightKey;

      const clipped = {
        ...base,
        schedule: clipBankLoanScheduleAtEarlyRepay(base.schedule, early),
      };

      const list = clipped.schedule || [];
      const inCurr: number[] = [];
      const inPrev: number[] = [];
      const inNext: number[] = [];
      list.forEach((row) => {
        if (inRange(row.repaymentDate, pf ?? undefined, pt ?? undefined)) {
          inCurr.push(row.instalmentNo);
        }
        if (inRange(row.repaymentDate, lyFrom ?? undefined, lyTo ?? undefined)) {
          inPrev.push(row.instalmentNo);
        }
        if (inRange(row.repaymentDate, nyFrom ?? undefined, nyTo ?? undefined)) {
          inNext.push(row.instalmentNo);
        }
      });
      setHighlightInstalments(inCurr);
      setHighlightInstalmentsLastYear(inPrev);
      setHighlightInstalmentsNextYear(inNext);

      const currFromDate = parseAnyDate(pf ?? undefined);
      const currToDate = parseAnyDate(pt ?? undefined);
      const periodToDate = parseAnyDate(pt || undefined);
      const nextPeriodFrom = periodToDate ? addDays(periodToDate, 1) : null;
      const nextPeriodTo = periodToDate ? addYears(periodToDate, 1) : null;

      let scheduleForSums = clipped.schedule;
      if (recalcSchedule.length > 0) {
        const recalcMapLocal: Record<number, LoanScheduleItemVO> = {};
        recalcSchedule.forEach((item) => { recalcMapLocal[item.instalmentNo] = item; });
        scheduleForSums = clipBankLoanScheduleAtEarlyRepay(
          clipped.schedule.map((row) => {
            const recalcRow = recalcMapLocal[row.instalmentNo];
            return recalcRow ? { ...recalcRow, repaymentDate: row.repaymentDate } : row;
          }),
          early,
        );
      }

      const highlighted = sumsFromScheduleHighlightWindows(
        scheduleForSums,
        inPrev,
        inNext,
        editedPrevFinalBalanceRef.current,
      );
      let sums: BankLoanSums | VehicleLoanSums;
      if (loanType === 'bank') {
        sums = {
          interestYoa: sumByRange(scheduleForSums, 'interestPayment', currFromDate, currToDate),
          currentLastYoa: sumByRange(scheduleForSums, 'principalPayment', currFromDate, currToDate),
          interestLastYoa: highlighted.interestLastYoa,
          currentYoa: highlighted.currentYoa,
          nonCurrentYoa: highlighted.nonCurrentYoa,
          interestNext: sumByRange(scheduleForSums, 'interestPayment', nextPeriodFrom, nextPeriodTo),
          balanceLastYoa: highlighted.balanceLastYoa,
        };
      } else {
        const currentLastYoa = sumByRange(scheduleForSums, 'principalPayment', currFromDate, currToDate);
        const interestYoa = sumByRange(scheduleForSums, 'interestPayment', currFromDate, currToDate);
        sums = {
          interestYoa,
          interestLastYoa: highlighted.interestLastYoa,
          currentYoa: highlighted.currentYoa,
          currentLastYoa,
          nonCurrentYoa: highlighted.nonCurrentYoa,
          balanceYoa: highlighted.currentYoa + highlighted.nonCurrentYoa,
          balanceLastYoa: highlighted.balanceLastYoa,
          nonCurrentLastYoa: (highlighted.balanceLastYoa ?? 0) - currentLastYoa,
        };
      }

      onDataLoadedRef.current?.({
        scheduleData: { ...clipped, schedule: scheduleForSums },
        sums,
        periods: {
          periodFrom: pf,
          periodTo: pt,
          lastYearPeriodFrom: lyFrom,
          lastYearPeriodTo: lyTo,
          nextYearPeriodFrom: nyFrom,
          nextYearPeriodTo: nyTo,
        },
        appliedRecalculatedSchedule: recalcSchedule.length > 0,
      });
    };

    // If directScheduleData is provided, use it directly without API call
    if (directScheduleData) {
      const clipped = {
        ...directScheduleData,
        schedule: clipBankLoanScheduleAtEarlyRepay(directScheduleData.schedule, earlyRepay),
      };
      setData(clipped);
      notifyHighlightsAndSums(clipped, []);
      return;
    }

    if (!scheduleRequest) return;

    // Split-mode one-shot needs period windows so backend can derive inCurr. 0 is a valid override.
    const wantsOneShotRecalc = !!(
      initialOverrides?.prevFinalBalance != null
      || initialOverrides?.currFinalBalance != null
      || initialOverrides?.editedInstalments
      || editedPrevFinalBalanceRef.current != null
      || editedCurrFinalBalanceRef.current != null
      || Object.keys(editedCurrInstalmentsRef.current).length > 0
    );
    if (wantsOneShotRecalc && periodFrom == null && periodTo == null) return;

    // Content key (not object identity). Non-one-shot omits periods so YOA load ≠ second API call.
    const fetchKey = [
      loanType,
      scheduleRequest.loanAmount,
      scheduleRequest.tenorYears,
      scheduleRequest.annualInterestRatePercent ?? '',
      scheduleRequest.firstRepaymentDate,
      scheduleRequest.totalInstalment ?? '',
      scheduleRequest.monthlyInstalment ?? '',
      scheduleRequest.interestFreeFrom ?? '',
      scheduleRequest.interestFreeTo ?? '',
      scheduleRequest.principalFreeFrom ?? '',
      scheduleRequest.principalFreeTo ?? '',
      earlyRepay ?? '',
      wantsOneShotRecalc ? (periodFrom ?? '') : '',
      wantsOneShotRecalc ? (periodTo ?? '') : '',
      wantsOneShotRecalc ? (lastYearPeriodFrom ?? '') : '',
      wantsOneShotRecalc ? (lastYearPeriodTo ?? '') : '',
      initialOverrides?.prevFinalBalance ?? '',
      initialOverrides?.currFinalBalance ?? '',
      initialOverrides?.editedInstalments ?? '',
    ].join('|');

    if (scheduleInitFetchKeyRef.current === fetchKey) {
      // Same init request already claimed. Periods arrived after fetch → local highlight only.
      const current = dataRef.current;
      if (current && !hasUserEditedRef.current) {
        notifyHighlightsAndSums(current, recalculatedDataRef.current || []);
      }
      return;
    }

    scheduleInitFetchKeyRef.current = fetchKey;
    const fetchGen = ++scheduleInitFetchGenRef.current;

    (async () => {
      try {
        // Build calc body; nest recalculate when overrides exist → one response with recalculatedSchedule.
        // null = unset (backend falls back to schedule row); 0 is a valid override.
        const prevOverride = editedPrevFinalBalanceRef.current != null
          ? editedPrevFinalBalanceRef.current
          : (initialOverrides?.prevFinalBalance ?? null);
        const currOverride = editedCurrFinalBalanceRef.current != null
          ? editedCurrFinalBalanceRef.current
          : (initialOverrides?.currFinalBalance ?? null);
        const instalmentsObj = Object.keys(editedCurrInstalmentsRef.current).length > 0
          ? editedCurrInstalmentsRef.current
          : (parsedInitialInstalments || {});
        const editedInstalmentsJson = Object.keys(instalmentsObj).length > 0
          ? JSON.stringify(instalmentsObj)
          : (initialOverrides?.editedInstalments ?? null);
        const hasOverride =
          prevOverride != null
          || currOverride != null
          || !!editedInstalmentsJson;

        const calcBody: LoanScheduleRequest = hasOverride
          ? {
              ...scheduleRequest,
              periodFrom,
              periodTo,
              lastYearPeriodFrom,
              lastYearPeriodTo,
              editedInstalments: editedInstalmentsJson,
              recalculate: {
                loanType,
                prevFinalBalance: prevOverride,
                currFinalBalance: currOverride,
                annualInterestRatePercent: scheduleRequest.annualInterestRatePercent ?? 0,
                currInstalments: [],
                remainingMonths: 0,
                startInstalmentNo: 0,
              },
            }
          : scheduleRequest;

        const resp = loanType === 'bank'
          ? await calcBankLoanSchedule(calcBody)
          : await calcVehicleLoanSchedule(calcBody);

        if (fetchGen !== scheduleInitFetchGenRef.current) return;

        const clipped = {
          ...resp,
          schedule: clipBankLoanScheduleAtEarlyRepay(resp?.schedule, earlyRepay),
        };
        setData(clipped);
        dataRef.current = clipped;

        // One-shot recalculated rows → Interest / Principal / Balance split columns
        const recalcSchedule = resp?.recalculatedSchedule || [];
        if (recalcSchedule.length > 0) {
          setRecalculatedData(recalcSchedule);
          recalculatedDataRef.current = recalcSchedule;
        } else {
          setRecalculatedData(null);
          recalculatedDataRef.current = null;
        }

        notifyHighlightsAndSums(clipped, recalcSchedule);
      } catch {
        if (fetchGen === scheduleInitFetchGenRef.current) {
          scheduleInitFetchKeyRef.current = null;
        }
      }
    })();
  // Primitive deps — avoid re-fetch on new scheduleRequest / initialOverrides object identity.
  // `data` omitted on purpose: setData must not re-enter; same-key path reads data when periods change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    loanType,
    earlyRepay,
    scheduleRequest?.loanAmount,
    scheduleRequest?.tenorYears,
    scheduleRequest?.annualInterestRatePercent,
    scheduleRequest?.firstRepaymentDate,
    scheduleRequest?.totalInstalment,
    scheduleRequest?.monthlyInstalment,
    scheduleRequest?.interestFreeFrom,
    scheduleRequest?.interestFreeTo,
    scheduleRequest?.principalFreeFrom,
    scheduleRequest?.principalFreeTo,
    periodFrom,
    periodTo,
    lastYearPeriodFrom,
    lastYearPeriodTo,
    nextYearPeriodFrom,
    nextYearPeriodTo,
    initialOverrides?.prevFinalBalance,
    initialOverrides?.currFinalBalance,
    initialOverrides?.editedInstalments,
    parsedInitialInstalments,
    directScheduleData,
  ]);
  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      sx={(theme) => ({
        zIndex: theme.zIndex.modal + 2,
        position: 'relative',
      })}
      ModalProps={{
        keepMounted: true,
        hideBackdrop: true, // no additional backdrop; dialog and drawer both interactive
        disableEnforceFocus: true, // allow focus to move between drawer and dialog
      }}
      PaperProps={{
        ref: paperRef,
        sx: { display: 'flex', flexDirection: 'column', height: '100%' },
      }}
    >
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">Repayment Schedule</Typography>
          {isRecalculating && <CircularProgress size={18} />}
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />

      {/* Summary */}
      <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, flexShrink: 0 }}>
        <Box>
          <Typography variant="button" color="text.secondary">Loan Amount</Typography>
          <Typography variant="body1" fontWeight={600}>{data ? number(data.loanAmount) : '-'}</Typography>
        </Box>
        <Box>
          <Typography variant="button" color="text.secondary">Repayment Period (Years)</Typography>
          <Typography variant="body1" fontWeight={600}>{data?.repaymentPeriodYears ?? '-'}</Typography>
        </Box>
        <Box>
          <Typography variant="button" color="text.secondary">Interest rate p.a. (%)</Typography>
          <Typography variant="body1" fontWeight={600}>{data?.interestRatePercent ?? '-'}</Typography>
        </Box>
        {/* Same row, right of Interest rate — SummaryCard hover trace sum (not inside the rate field) */}
        <Box sx={{display: 'flex', alignItems: 'flex-end', minHeight: 40}}>
          {summaryTrace != null && summaryTraceSum != null ? (
            <Typography
              variant="body1"
              fontWeight={700}
              sx={{
                color: 'error.main',
                animation: 'bankLoanSummaryTracePulse 1.1s ease-in-out infinite',
                '@keyframes bankLoanSummaryTracePulse': {
                  '0%, 100%': {opacity: 0.55},
                  '50%': {opacity: 1},
                },
              }}
            >
              Σ {number(summaryTraceSum)}
            </Typography>
          ) : null}
        </Box>
      </Box>

      <Divider />

      {/* Schedule Table */}
      <Box sx={{ p: 2, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <TableContainer ref={tableContainerRef} sx={{ flex: 1, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell align="left" sx={{ backgroundColor: 'background.paper' }}>Terms</TableCell>
                <TableCell align="left" sx={{ backgroundColor: 'background.paper' }}>Repayment Date</TableCell>
                <TableCell align="left" sx={{ backgroundColor: 'background.paper' }}>Instalment</TableCell>
                <TableCell align="left" sx={{ backgroundColor: 'background.paper' }}>Interest</TableCell>
                <TableCell align="left" sx={{ backgroundColor: 'background.paper' }}>Principal</TableCell>
                <TableCell align="left" sx={{ backgroundColor: 'background.paper' }}>Balance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data?.schedule || []).map((row) => {
                const inCurr = highlightInstalments.includes(row.instalmentNo);
                const inPrev = highlightInstalmentsLastYear.includes(row.instalmentNo);
                const inNext = highlightInstalmentsNextYear.includes(row.instalmentNo);
                const isLastPrev = row.instalmentNo === lastPrevNo;
                const isLastCurr = row.instalmentNo === lastCurrNo;
                const recalcRow = recalcMap[row.instalmentNo];
                const rowTraced = isTraceRow(row.instalmentNo);
                const traceInstalment = rowTraced && summaryTrace?.column === 'instalment';
                const traceInterest = rowTraced && summaryTrace?.column === 'interest';
                const tracePrincipal = rowTraced && summaryTrace?.column === 'principal';
                const traceBalance = rowTraced && summaryTrace?.column === 'balance';
                const bg = inCurr
                  ? 'rgba(76, 175, 80, 0.15)'
                  : inPrev
                    ? 'rgba(33, 150, 243, 0.15)'
                    : inNext
                      ? 'rgba(255, 193, 7, 0.2)'
                      : undefined;
                const fw = inCurr || inPrev || inNext ? 600 : undefined;
                // Display row ≈ 33.02px; compact split MoneyField = 28px → cell py ≈ 2.51px each side.
                const hasSplitMoneyField = !readOnly && (inCurr || isLastPrev || isLastCurr);
                const splitRowCellSx = hasSplitMoneyField
                  ? { fontWeight: fw, py: '2.51px', verticalAlign: 'middle' as const }
                  : { fontWeight: fw };
                const splitMoneyFieldSx = { m: 0 } as const;
                return (
                  <TableRow
                    key={row.instalmentNo}
                    data-instalment-no={row.instalmentNo}
                    hover
                    sx={{
                      backgroundColor: bg,
                      ...(hasSplitMoneyField
                        ? {
                            '& > .MuiTableCell-root': {
                              paddingTop: '2.51px',
                              paddingBottom: '2.51px',
                              verticalAlign: 'middle',
                            },
                          }
                        : null),
                    }}
                  >
                    <TableCell align="left" sx={hasSplitMoneyField ? splitRowCellSx : undefined}>{row.instalmentNo}</TableCell>
                    <TableCell align="left" sx={splitRowCellSx}>{row.repaymentDate}</TableCell>
                    {/* Instalment: editable for all inCurr rows (unless readOnly) */}
                    <TableCell align="left" sx={{...splitRowCellSx, ...(traceInstalment ? TRACE_CELL_SX : null)}}>
                      {inCurr && !readOnly ? (
                        <MoneyField
                          value={row.instalmentAmount}
                          showCents
                          compact
                          separator=">"
                          suffixValue={editedCurrInstalments[row.instalmentNo] ?? 0}
                          forceShowSplitMode={row.instalmentNo in editedCurrInstalments}
                          icon={<EditIcon fontSize="small" />}
                          cancelIcon={<CloseIcon fontSize="small" />}
                          onSuffixChange={(n) => handleInstalmentChange(row.instalmentNo, n)}
                          onEditCancel={() => handleInstalmentCancel(row.instalmentNo)}
                          sx={splitMoneyFieldSx}
                        />
                      ) : recalcRow ? (
                        <Box sx={{ fontSize: 12 }}>
                          {number(row.instalmentAmount)} <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>{' → '}{number(recalcRow.instalmentAmount)}</Box>
                        </Box>
                      ) : number(row.instalmentAmount)}
                    </TableCell>
                    {/* Interest: show old → new when recalculated */}
                    <TableCell align="left" sx={{...splitRowCellSx, ...(traceInterest ? TRACE_CELL_SX : null)}}>
                      {recalcRow ? (
                        <Box sx={{ fontSize: 12 }}>
                          {number(row.interestPayment)} <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>{' → '}{number(recalcRow.interestPayment)}</Box>
                        </Box>
                      ) : number(row.interestPayment)}
                    </TableCell>
                    {/* Principal: show old → new when recalculated */}
                    <TableCell align="left" sx={{...splitRowCellSx, ...(tracePrincipal ? TRACE_CELL_SX : null)}}>
                      {recalcRow ? (
                        <Box sx={{ fontSize: 12 }}>
                          {number(row.principalPayment)} <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>{' → '}{number(recalcRow.principalPayment)}</Box>
                        </Box>
                      ) : number(row.principalPayment)}
                    </TableCell>
                    {/* Balance: editable for last inPrev and last inCurr (unless readOnly); show old → new when recalculated */}
                    <TableCell align="left" sx={{...splitRowCellSx, ...(traceBalance ? TRACE_CELL_SX : null)}}>
                      {isLastPrev && !readOnly ? (
                        <MoneyField
                          value={row.outstandingBalance}
                          showCents
                          compact
                          separator=">"
                          suffixValue={editedPrevFinalBalance ?? 0}
                          forceShowSplitMode={editedPrevFinalBalance != null}
                          icon={<EditIcon fontSize="small" />}
                          cancelIcon={<CloseIcon fontSize="small" />}
                          onSuffixChange={(n) => handlePrevFinalBalanceChange(n)}
                          onEditCancel={() => handlePrevFinalBalanceCancel()}
                          sx={splitMoneyFieldSx}
                        />
                      ) : isLastCurr && !readOnly ? (
                        <MoneyField
                          value={row.outstandingBalance}
                          showCents
                          compact
                          separator=">"
                          suffixValue={editedCurrFinalBalance ?? 0}
                          forceShowSplitMode={editedCurrFinalBalance != null}
                          icon={<EditIcon fontSize="small" />}
                          cancelIcon={<CloseIcon fontSize="small" />}
                          onSuffixChange={(n) => handleCurrFinalBalanceChange(n)}
                          onEditCancel={() => handleCurrFinalBalanceCancel()}
                          sx={splitMoneyFieldSx}
                        />
                      ) : recalcRow ? (
                        <Box sx={{ fontSize: 12 }}>
                          {number(row.outstandingBalance)} <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>{' → '}{number(recalcRow.outstandingBalance)}</Box>
                        </Box>
                      ) : number(row.outstandingBalance)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Drawer>
  );
}

